import React, { useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { AppShell } from '../../components/layout/AppShell';
import { PageLayout } from '../../components/common/PageLayout';
import { ContentCard } from '../../components/common/ContentCard';
import { Button } from '../../components/common/Button';
import { PAGE_COLORS } from '../../utils/pageColors';
import type { ProductionLineStage } from '../../utils/types';
import { 
  Settings, BarChart, Square, Box, FileImage, 
  AlertTriangle 
} from 'lucide-react';
import { ProductionLineTabNavigation } from '../../components/production-line/ProductionLineTabNavigation';
import { ProductionLineFilters } from '../../components/production-line/ProductionLineFilters';
import { ProductionLineRowColorGuide } from '../../components/production-line/ProductionLineRowColorGuide';
import { OrderDetailsTable } from '../../components/common/OrderDetailsTable';
import { CompleteJobConfirmDialog } from '../../components/production-line/CompleteJobConfirmDialog';
import { isOrderLeaded } from '../../utils/leadedDetection';
import { getDashboardJobsReadyForComplete } from '../../utils/productionLineHelpers';
import { getProductionLineRowClass } from '../../utils/productionLineRowStyles';
import { getProductionLineColumns } from './config';
import { 
  useGetProductionLineDashboardQuery,
  useBulkCompleteProductionLineDashboardMutation,
  useGetProductionLineSealingQuery,
  useGetProductionLineGlassCuttingQuery,
  useGetProductionLineSpacerQuery,
  useGetProductionLineDrawingQuery,
  useCompleteSealingMutation,
  useSendBackSealingMutation,
  useCompleteCuttingMutation,
  useSendBackGlassCuttingMutation,
  useCompleteSpacerCuttingMutation,
  useCompleteDrawingMutation,
  useGetGlassValuesDropdownQuery,
  useCompleteProductionMutation
} from '../../store/slices/apiSlice';
// import { ProductionLineJobCard } from '../../components/production-line/ProductionLineJobCard';
import { useToast } from '../../hooks/useToast';
import type { ProductionLinePageProps } from '../../types';

const PRODUCTION_LINE_TABS: Array<{
  key: ProductionLineStage;
  label: string; 
  icon: React.ComponentType<any>;
  color: string;
}> = [
  { key: 'dashboard', label: 'Dashboard', icon: BarChart, color: 'indigo' },
  { key: 'sealing_allocation', label: 'Sealing & Allocation', icon: Settings, color: 'blue' },
  { key: 'glass_cutting', label: 'Glass Cutting', icon: Square, color: 'green' },
  { key: 'spacer_cutting', label: 'Spacer Bar Cutting', icon: Box, color: 'orange' },
  { key: 'drawing', label: 'Drawing', icon: FileImage, color: 'purple' },
];

function collectSpacerFilterOptionsFromJobs(jobs: Array<{ orders?: any[] }>) {
  const flat = jobs.flatMap((j) => j.orders ?? []);
  const uniq = (field: string) => {
    const set = new Set<string>();
    for (const o of flat) {
      const v = o[field];
      if (v === null || v === undefined) continue;
      const s = String(v).trim();
      if (s !== '') set.add(s);
    }
    return Array.from(set).sort((a, b) =>
      a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' })
    );
  };
  return {
    spacerBarTypeOptions: uniq('spacer_bar_type'),
    spacerBarColorOptions: uniq('spacer_bar_color'),
    spacerBarThicknessOptions: uniq('spacer_bar_thickness_mm'),
    spacerBar2TypeOptions: uniq('spacer_bar2_type'),
    spacerBar2ColorOptions: uniq('spacer_bar2_color'),
    spacerBar2ThicknessOptions: uniq('spacer_bar2_thickness_mm'),
  };
}

export const ProductionLinePage: React.FC<ProductionLinePageProps> = () => {
  const { tab } = useParams<{ tab?: ProductionLineStage }>();
  const toast = useToast();
  
  const [activeTab, setActiveTab] = useState<ProductionLineStage>(
    tab || 'dashboard'
  );
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDate, setSelectedDate] = useState<string>(
    new Date().toISOString().split('T')[0] // Today
  );
  const [jobTypeFilter, setJobTypeFilter] = useState('all');
  const [glassCuttingProductTypeFilter, setGlassCuttingProductTypeFilter] = useState('all');
  const [spacerBarTypeFilter, setSpacerBarTypeFilter] = useState('all');
  const [spacerBarColorFilter, setSpacerBarColorFilter] = useState('all');
  const [spacerBarThicknessFilter, setSpacerBarThicknessFilter] = useState('all');
  const [spacerBar2TypeFilter, setSpacerBar2TypeFilter] = useState('all');
  const [spacerBar2ColorFilter, setSpacerBar2ColorFilter] = useState('all');
  const [spacerBar2ThicknessFilter, setSpacerBar2ThicknessFilter] = useState('all');
  const [showCompleteDialog, setShowCompleteDialog] = useState(false);
  const [selectedJobForComplete, setSelectedJobForComplete] = useState<{ jobId: string; jobNumber: string } | null>(null);
  const [showBulkCompleteDialog, setShowBulkCompleteDialog] = useState(false);
  const [bulkJobIdsSnapshot, setBulkJobIdsSnapshot] = useState<string[]>([]);
  const [bulkJobLabels, setBulkJobLabels] = useState<Array<{ id: string; website_job_number?: string | null }>>([]);

  // Common filter parameters (full list per tab; no pagination)
  const filters = useMemo(() => ({
    manufacture_date: selectedDate,
    search: searchTerm || undefined,
    job_type: jobTypeFilter !== 'all' ? jobTypeFilter : undefined,
    ordering: activeTab === 'glass_cutting' ? '-priority,manufacture_date,-created_at' : 'manufacture_date,-created_at',
  }), [selectedDate, searchTerm, jobTypeFilter, activeTab]);

  // Tab-specific data fetching - optimized for real-time updates
  const { data: dashboardData, isLoading: dashboardLoading, isError: dashboardError } = useGetProductionLineDashboardQuery(filters, {
    skip: activeTab !== 'dashboard',
    refetchOnMountOrArgChange: true, // Always fetch fresh data
  });
  const { data: sealingData, isLoading: sealingLoading, isError: sealingError } = useGetProductionLineSealingQuery(filters, {
    skip: activeTab !== 'sealing_allocation',
    refetchOnMountOrArgChange: true, // Always fetch fresh data  
  });
  const { data: glassCuttingData, isLoading: glassCuttingLoading, isError: glassCuttingError } = useGetProductionLineGlassCuttingQuery(filters, {
    skip: activeTab !== 'glass_cutting',
    refetchOnMountOrArgChange: true,
  });
  const { data: spacerData, isLoading: spacerLoading, isError: spacerError } = useGetProductionLineSpacerQuery(filters, {
    skip: activeTab !== 'spacer_cutting',
    refetchOnMountOrArgChange: true,
  });
  const { data: drawingData, isLoading: drawingLoading, isError: drawingError } = useGetProductionLineDrawingQuery(filters, {
    skip: activeTab !== 'drawing',
    refetchOnMountOrArgChange: true,
  });

  // Mutations
  const [completeSealing] = useCompleteSealingMutation();
  const [sendBackSealing] = useSendBackSealingMutation();
  const [completeCutting] = useCompleteCuttingMutation();
  const [sendBackGlassCutting] = useSendBackGlassCuttingMutation();
  const [completeSpacerCutting] = useCompleteSpacerCuttingMutation();
  const [completeDrawing] = useCompleteDrawingMutation();
  const [completeProduction, { isLoading: isCompleting }] = useCompleteProductionMutation();
  const [bulkCompleteProduction, { isLoading: isBulkCompleting }] = useBulkCompleteProductionLineDashboardMutation();

  // Get current tab data
  const getCurrentTabData = () => {
    switch (activeTab) {
      case 'dashboard':
        return { data: dashboardData, isLoading: dashboardLoading, isError: dashboardError };
      case 'sealing_allocation':
        return { data: sealingData, isLoading: sealingLoading, isError: sealingError };
      case 'glass_cutting':
        return { data: glassCuttingData, isLoading: glassCuttingLoading, isError: glassCuttingError };
      case 'spacer_cutting':
        return { data: spacerData, isLoading: spacerLoading, isError: spacerError };
      case 'drawing':
        return { data: drawingData, isLoading: drawingLoading, isError: drawingError };
      default:
        return { data: undefined, isLoading: false, isError: true };
    }
  };

  const { data, isLoading, isError } = getCurrentTabData();
  const jobs = data?.results ?? [];

  const spacerFilterOptions = useMemo(
    () =>
      activeTab === 'spacer_cutting'
        ? collectSpacerFilterOptionsFromJobs(jobs)
        : {
            spacerBarTypeOptions: [] as string[],
            spacerBarColorOptions: [] as string[],
            spacerBarThicknessOptions: [] as string[],
            spacerBar2TypeOptions: [] as string[],
            spacerBar2ColorOptions: [] as string[],
            spacerBar2ThicknessOptions: [] as string[],
          },
    [activeTab, jobs]
  );
  
  // Fetch glass values for GAP detection (Condition Two needs lookup when *_is_arrival fields aren't available)
  const { data: glassValuesData } = useGetGlassValuesDropdownQuery({});
  
  // Create lookup map from glass values data
  const glassValuesLookup = useMemo(() => {
    if (glassValuesData?.results) {
      const lookup = new Map<string, { is_arrival: boolean; is_cutting: boolean }>();
      glassValuesData.results.forEach((gv) => {
        // Ensure key is a string (glass value ID)
        const key = String(gv.value);
        lookup.set(key, { is_arrival: gv.is_arrival, is_cutting: gv.is_cutting });
      });
      return lookup;
    }
    return undefined;
  }, [glassValuesData]);
  
  // Extract and filter orders from jobs for table display (Phase 5A: Table Integration)
  const orders = useMemo(() => {
    // Step 1: Sort jobs by potential_delivery_date (earliest first)
    const sortedJobs = [...jobs].sort((a, b) => {
      const dateA = a.potential_delivery_date ? new Date(a.potential_delivery_date).getTime() : Infinity;
      const dateB = b.potential_delivery_date ? new Date(b.potential_delivery_date).getTime() : Infinity;
      return dateA - dateB; // Ascending order (earliest date first)
    });
    
    const seenJobIds = new Set<string>();
    
    // Step 2: For each job, sort its orders by created_at (oldest first)
    return sortedJobs.flatMap(job => {
      // Sort orders within this job by created_at (ascending - oldest first)
      const sortedOrders = [...job.orders].sort((a: any, b: any) => {
        const dateA = a.created_at ? new Date(a.created_at).getTime() : 0;
        const dateB = b.created_at ? new Date(b.created_at).getTime() : 0;
        return dateA - dateB; // Ascending order (oldest first)
      });

      let ordersForTab = sortedOrders;
      if (activeTab === 'glass_cutting' && glassCuttingProductTypeFilter !== 'all') {
        ordersForTab = sortedOrders.filter(
          (o: any) => o.product_type === glassCuttingProductTypeFilter
        );
      }
      if (activeTab === 'spacer_cutting') {
        ordersForTab = sortedOrders.filter((o: any) => {
          if (spacerBarTypeFilter !== 'all' && String(o.spacer_bar_type ?? '').trim() !== spacerBarTypeFilter) {
            return false;
          }
          if (spacerBarColorFilter !== 'all' && String(o.spacer_bar_color ?? '').trim() !== spacerBarColorFilter) {
            return false;
          }
          if (
            spacerBarThicknessFilter !== 'all' &&
            String(o.spacer_bar_thickness_mm ?? '').trim() !== spacerBarThicknessFilter
          ) {
            return false;
          }
          if (spacerBar2TypeFilter !== 'all' && String(o.spacer_bar2_type ?? '').trim() !== spacerBar2TypeFilter) {
            return false;
          }
          if (
            spacerBar2ColorFilter !== 'all' &&
            String(o.spacer_bar2_color ?? '').trim() !== spacerBar2ColorFilter
          ) {
            return false;
          }
          if (
            spacerBar2ThicknessFilter !== 'all' &&
            String(o.spacer_bar2_thickness_mm ?? '').trim() !== spacerBar2ThicknessFilter
          ) {
            return false;
          }
          return true;
        });
      }

      return ordersForTab.map((order: any) => {
        const isFirstOrderFromJob = !seenJobIds.has(job.id);
        if (isFirstOrderFromJob) {
          seenJobIds.add(job.id);
        }
        
        return {
          ...order,
          // Add job context for reference
          job_id: job.id,
          customer_name: job.customer_name,
          website_job_number: job.website_job_number,
          delivery_postcode: job.delivery_postcode,
          manufacture_date: job.manufacture_date,
          glass_arrival_date: job.glass_arrival_date,
          potential_delivery_date: job.potential_delivery_date,
          job_priority: job.priority,
          job_type: job.job_type,
          job_status: job.status,
          job_production_status: job.production_status,
          lock_data: job.lock_data,
          // Add job grouping information for date editing
          is_first_order_from_job: isFirstOrderFromJob,
          // Add completion status from our metadata system
          glass_arrived_completed: order.metadata?.glass_arrival_workflow?.arrived || false,
          glass_cutting_completed: order.metadata?.glass_cutting_status?.completed || false,
          sealing_completed: order.metadata?.sealing_status?.completed || false,
          spacer_cutting_completed: order.metadata?.spacer_cutting_status?.completed || false,
          drawing_completed: order.metadata?.drawing_work_status?.completed || false,
          // Add workstation relevance indicators
          is_sealing_relevant: order.product_type === 'double' || order.product_type === 'triple',
          is_glass_cutting_relevant: job.job_type === 'gc' || order.glass_width || order.glass_height,
          is_spacer_relevant: order.product_type === 'double' || order.product_type === 'triple',
          is_drawing_relevant: job.job_type === 'gap' || order.metadata?.cutout_shape || order.metadata?.hole_size_mm || (order.images && order.images.length > 0)
        };
      });
    });
  }, [
    jobs,
    activeTab,
    glassCuttingProductTypeFilter,
    spacerBarTypeFilter,
    spacerBarColorFilter,
    spacerBarThicknessFilter,
    spacerBar2TypeFilter,
    spacerBar2ColorFilter,
    spacerBar2ThicknessFilter,
  ]);

  const dashboardReadyJobs = useMemo(
    () => (activeTab === 'dashboard' ? getDashboardJobsReadyForComplete(orders) : []),
    [activeTab, orders]
  );
  const readyCompleteCount = dashboardReadyJobs.length;
  
  // Phase 5A: Show all orders on all tabs (matching existing system)
  // Tab-specific filtering will be added later when business logic is defined
  // For now, all tabs show same data with different completion tracking

  // Handle order actions
  const handleOrderAction = async (orderId: string, action: string) => {
    try {
      switch (activeTab) {
        case 'sealing_allocation':
          if (action === 'completed') {
            await completeSealing({ orderId, notes: 'Sealing completed from production line' }).unwrap();
            toast.success('Sealing Complete', 'Order sealing has been marked as completed');
          }
          break;
        case 'glass_cutting':
          if (action === 'completed') {
            await completeCutting({ orderId, notes: 'Glass cutting completed' }).unwrap();
            toast.success('Cutting Complete', 'Glass cutting has been completed');
          }
          break;
        case 'spacer_cutting':
          if (action === 'completed') {
            await completeSpacerCutting({ orderId, notes: 'Spacer cutting completed' }).unwrap();
            toast.success('Spacer Complete', 'Spacer bar cutting has been completed');
          }
          break;
        case 'drawing':
          if (action === 'completed') {
            await completeDrawing({ orderId, notes: 'Drawing work completed' }).unwrap();
            toast.success('Drawing Complete', 'Drawing work has been completed');
          }
          break;
      }
    } catch (error: any) {
      toast.error('Action Failed', error?.data?.detail || 'Please try again');
    }
  };

  // Handle send back actions (dashboard only)
  const handleSendBack = async (orderId: string, type: 'sealing' | 'glass_cutting') => {
    try {
      const reason = 'Sent back for corrections';
      
      if (type === 'sealing') {
        await sendBackSealing({ orderId, reason }).unwrap();
        toast.success('Sent Back to Sealing', 'Order has been sent back to sealing & allocation workflow');
      } else if (type === 'glass_cutting') {
        await sendBackGlassCutting({ orderId, reason }).unwrap();
        toast.success('Sent Back to Glass Cutting', 'Order has been sent back to glass cutting workflow');
      }
    } catch (error: any) {
      toast.error('Send Back Failed', error?.data?.detail || 'Please try again');
    }
  };

  // Expose handleSendBack to OrderTableRow component
  React.useEffect(() => {
    if (typeof window !== 'undefined') {
      (window as any).handleSendBack = handleSendBack;
    }
    return () => {
      if (typeof window !== 'undefined') {
        delete (window as any).handleSendBack;
      }
    };
  }, [handleSendBack]);

  // Handler for triggering complete production dialog
  const handleTriggerCompleteDialog = (jobId: string, jobNumber: string) => {
    setSelectedJobForComplete({ jobId, jobNumber });
    setShowCompleteDialog(true);
  };

  // Handler for completing production
  const handleCompleteProduction = async () => {
    if (!selectedJobForComplete) return;
    
    try {
      await completeProduction({ jobId: selectedJobForComplete.jobId }).unwrap();
      setShowCompleteDialog(false);
      setSelectedJobForComplete(null);
      toast.success('Production Complete', `Job ${selectedJobForComplete.jobNumber} marked as production complete`);
    } catch (e: any) {
      toast.error('Failed to complete', e?.data?.detail || 'Please try again');
    }
  };

  const openBulkCompleteDialog = () => {
    setBulkJobIdsSnapshot(dashboardReadyJobs.map((j) => j.id));
    setBulkJobLabels(dashboardReadyJobs);
    setShowBulkCompleteDialog(true);
  };

  const handleBulkCompleteProduction = async () => {
    if (bulkJobIdsSnapshot.length === 0) return;
    try {
      const res = await bulkCompleteProduction({ jobIds: bulkJobIdsSnapshot }).unwrap();
      setShowBulkCompleteDialog(false);
      setBulkJobIdsSnapshot([]);
      setBulkJobLabels([]);
      const { succeeded_count, failed_count } = res;
      if (failed_count > 0) {
        toast.success(
          'Bulk production complete',
          `Completed ${succeeded_count} job(s). ${failed_count} could not be completed.`
        );
      } else {
        toast.success('Bulk production complete', `Completed ${succeeded_count} job(s).`);
      }
    } catch (e: any) {
      toast.error('Bulk complete failed', e?.data?.detail || 'Please try again');
    }
  };


  if (isError) {
    return (
      <AppShell>
        <PageLayout
          title="Production Line"
          subtitle="Manufacturing workstation management"
          bgColor={PAGE_COLORS.productionLine.bgColor}
          textColor={PAGE_COLORS.productionLine.textColor}
          borderColor={PAGE_COLORS.productionLine.borderColor}
          icon={<Settings className="w-6 h-6" />}
        >
          <ContentCard title="Error Loading Production Line">
            <div className="text-center py-6">
              <AlertTriangle className="mx-auto h-12 w-12 text-red-500 mb-4" />
              <p className="text-gray-600 mb-4">Unable to load production line data</p>
              <Button onClick={() => window.location.reload()}>Try Again</Button>
            </div>
          </ContentCard>
        </PageLayout>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <PageLayout
        title="Production Line"
        subtitle="Manufacturing workstation management"
        bgColor={PAGE_COLORS.productionLine.bgColor}
        textColor={PAGE_COLORS.productionLine.textColor}
        borderColor={PAGE_COLORS.productionLine.borderColor}
        icon={<Settings className="w-6 h-6" />}
      >
        {/* Tab Navigation */}
        <ProductionLineTabNavigation 
          tabs={PRODUCTION_LINE_TABS}
          activeTab={activeTab}
          onTabChange={(tab) => {
            setActiveTab(tab);
          }}
        />
        
        {/* Common Filters */}
        <ProductionLineFilters
          searchTerm={searchTerm}
          selectedDate={selectedDate}
          jobTypeFilter={jobTypeFilter}
          onSearchChange={setSearchTerm}
          onDateChange={setSelectedDate}
          onJobTypeChange={setJobTypeFilter}
          productTypeFilter={glassCuttingProductTypeFilter}
          onProductTypeFilterChange={setGlassCuttingProductTypeFilter}
          showProductTypeFilter={activeTab === 'glass_cutting'}
          showSpacerFilters={activeTab === 'spacer_cutting'}
          spacerBarTypeFilter={spacerBarTypeFilter}
          spacerBarTypeOptions={spacerFilterOptions.spacerBarTypeOptions}
          onSpacerBarTypeFilterChange={setSpacerBarTypeFilter}
          spacerBarColorFilter={spacerBarColorFilter}
          spacerBarColorOptions={spacerFilterOptions.spacerBarColorOptions}
          onSpacerBarColorFilterChange={setSpacerBarColorFilter}
          spacerBarThicknessFilter={spacerBarThicknessFilter}
          spacerBarThicknessOptions={spacerFilterOptions.spacerBarThicknessOptions}
          onSpacerBarThicknessFilterChange={setSpacerBarThicknessFilter}
          spacerBar2TypeFilter={spacerBar2TypeFilter}
          spacerBar2TypeOptions={spacerFilterOptions.spacerBar2TypeOptions}
          onSpacerBar2TypeFilterChange={setSpacerBar2TypeFilter}
          spacerBar2ColorFilter={spacerBar2ColorFilter}
          spacerBar2ColorOptions={spacerFilterOptions.spacerBar2ColorOptions}
          onSpacerBar2ColorFilterChange={setSpacerBar2ColorFilter}
          spacerBar2ThicknessFilter={spacerBar2ThicknessFilter}
          spacerBar2ThicknessOptions={spacerFilterOptions.spacerBar2ThicknessOptions}
          onSpacerBar2ThicknessFilterChange={setSpacerBar2ThicknessFilter}
        />

        <ProductionLineRowColorGuide activeTab={activeTab} />

        {activeTab === 'dashboard' && (
          <div className="flex justify-end mb-4">
            <Button
              type="button"
              disabled={readyCompleteCount === 0 || dashboardLoading}
              onClick={openBulkCompleteDialog}
              className="bg-green-600 hover:bg-green-700 text-white disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {dashboardLoading
                ? 'Loading…'
                : `Complete ${readyCompleteCount} job${readyCompleteCount === 1 ? '' : 's'}`}
            </Button>
          </div>
        )}

        {/* Main Content - Table-based display matching existing system */}
        <ContentCard
          title={`${PRODUCTION_LINE_TABS.find(t => t.key === activeTab)?.label} Jobs`}
          subtitle={`Manufacturing orders for ${new Date(selectedDate).toLocaleDateString()}`}
        >
          <OrderDetailsTable
            orders={orders}
            loading={isLoading}
            mode="view"
            rackContext="production_line"
            config={{
              // Production Line: ALL order details + job context + completion tracking
              // Columns are now managed in ./config/index.ts for better maintainability
              columns: getProductionLineColumns(activeTab),
              features: {
                autoHideEmptyColumns: true, // Hide columns where ALL rows are empty/null/"-"  
                responsive: true,
                jobGroupTotals: ['dashboard', 'sealing_allocation', 'glass_cutting'].includes(activeTab),
              },
              styling: {
                rowClass: (order: any) =>
                  getProductionLineRowClass(order, activeTab, glassValuesLookup),
              }
            }}
            customActions={[
              // Workstation-specific actions based on active tab
              ...(activeTab === 'sealing_allocation' ? [{
                label: 'Complete Sealing',
                onClick: (order: any) => handleOrderAction(order.id, 'completed'),
                variant: 'primary' as const,
                condition: (order: any) => {
                  // Only show Complete Sealing if:
                  // 1. Not already completed
                  // 2. Order is marked as sealed
                  // 3. Rack field has a value
                  const isSealed = order.metadata?.sealing_workflow?.sealed ?? false;
                  const hasRack = order.rack && order.rack.trim() !== '';
                  return !order.sealing_completed && isSealed && hasRack;
                }
              }] : []),
              ...(activeTab === 'glass_cutting' ? [{
                label: 'Complete Cutting',
                onClick: (order: any) => handleOrderAction(order.id, 'completed'),
                variant: 'primary' as const,
                condition: (order: any) => {
                  // Only show Complete Cutting if not already completed and checkbox is checked
                  const isCut = order.metadata?.glass_cutting_workflow?.cut_completed ?? false;
                  
                  // Different requirements based on product type:
                  // - Double/Triple: Just need checkbox checked
                  // - Single/Mirror + Leaded: Need checkbox checked AND temporary_rack value entered
                  // - Single/Mirror (not leaded): Need checkbox checked AND rack value entered
                  const isDoubleOrTriple = order.product_type === 'double' || order.product_type === 'triple';
                  
                  if (isDoubleOrTriple) {
                    // Double/Triple: Show complete button immediately when checkbox is checked
                    return !order.glass_cutting_completed && isCut;
                  } else {
                    // Single/Mirror: Check for rack or temporary_rack based on leaded status
                    const isLeaded = isOrderLeaded(order);
                    
                    if (isLeaded) {
                      // Leaded orders use temporary_rack
                      const hasTempRack = order.temporary_rack && order.temporary_rack.trim() !== '';
                      return !order.glass_cutting_completed && isCut && hasTempRack;
                    } else {
                      // Non-leaded orders use regular rack
                      const hasRack = order.rack && order.rack.trim() !== '';
                      return !order.glass_cutting_completed && isCut && hasRack;
                    }
                  }
                }
              }] : []),
              // No actions for spacer_cutting tab - only checkbox workflow
              ...(activeTab === 'drawing' ? [{
                label: 'Complete Drawing',
                onClick: (order: any) => handleOrderAction(order.id, 'completed'),
                variant: 'primary' as const,
                condition: (order: any) => {
                  // Drawing completion logic:
                  // SINGLE glazed: checkbox + rack (non-leaded) or temporary_rack (leaded)
                  // DOUBLE/TRIPLE glazed: Show when checkbox checked (no rack requirement)
                  const isDone = order.metadata?.drawing_workflow?.done ?? false;
                  const isSingle = order.product_type === 'single';
                  const isDoubleOrTriple = order.product_type === 'double' || order.product_type === 'triple';

                  if (order.drawing_completed) return false;
                  if (!isDone) return false;

                  if (isSingle) {
                    if (isOrderLeaded(order)) {
                      const hasTempRack =
                        order.temporary_rack && String(order.temporary_rack).trim() !== '';
                      return hasTempRack;
                    }
                    const hasRack = order.rack && String(order.rack).trim() !== '';
                    return hasRack;
                  }

                  if (isDoubleOrTriple) {
                    return true;
                  }

                  return false;
                }
              }] : [])
            ]}
            className="w-full"
            onTriggerCompleteDialog={handleTriggerCompleteDialog}
          />
        </ContentCard>
      </PageLayout>
      
      {/* Confirmation dialog for completing production - rendered once at page level */}
      {showCompleteDialog && selectedJobForComplete && (
        <CompleteJobConfirmDialog
          isOpen={showCompleteDialog}
          jobNumber={selectedJobForComplete.jobNumber}
          onConfirm={handleCompleteProduction}
          onCancel={() => {
            setShowCompleteDialog(false);
            setSelectedJobForComplete(null);
          }}
          isLoading={isCompleting}
        />
      )}

      {showBulkCompleteDialog && (
        <CompleteJobConfirmDialog
          isOpen={showBulkCompleteDialog}
          title={`Complete production for ${bulkJobLabels.length} jobs?`}
          leadContent={
            <div className="space-y-2">
              <p className="text-gray-600 text-sm">
                You are about to mark <strong className="text-gray-900">{bulkJobLabels.length}</strong> job
                {bulkJobLabels.length === 1 ? '' : 's'} as production complete — the same outcome as using
                Complete on each job.
              </p>
              <div className="max-h-36 overflow-y-auto border border-gray-200 rounded-md p-2 bg-gray-50 text-sm text-gray-800">
                {bulkJobLabels.map((j) => (
                  <div key={j.id}>{j.website_job_number || j.id}</div>
                ))}
              </div>
            </div>
          }
          panelClassName="max-w-lg"
          onConfirm={handleBulkCompleteProduction}
          onCancel={() => {
            setShowBulkCompleteDialog(false);
            setBulkJobIdsSnapshot([]);
            setBulkJobLabels([]);
          }}
          isLoading={isBulkCompleting}
          confirmLabel="Yes, complete all"
          confirmingLabel="Completing…"
        />
      )}
    </AppShell>
  );
};
